// OrderDetailModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import StatusBadge from "./StatusBadge";
import { STATUS_META, ORDER_TYPE_META } from "../utils/statusMaps";
import OrderActions from "./OrderActions";
import { Loader2 } from "lucide-react";
import "../../../App.css";

/* -------------------------------------------------------------------------- */
/*  Pure helpers (moved outside so they are not recreated every render)       */
/* -------------------------------------------------------------------------- */

const Row = ({ label, value }) => (
  <div className="rounded-md bg-slate-50 px-2.5 py-2">
    <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
      {label}
    </div>

    <div className="mt-0.5 truncate text-[9px] font-semibold text-slate-800">
      {value || "-"}
    </div>
  </div>
);

const parseCustomization = (rawCustomization) => {
  if (!rawCustomization) return null;

  try {
    let parsed = rawCustomization;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (typeof parsed !== "string") break;
      parsed = JSON.parse(parsed);
    }
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("Customization parse error:", error, rawCustomization);
    return null;
  }
};

const formatLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatEventDate = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

const formatEventTime = (value) => {
  if (!value) return "";
  const [hourValue, minuteValue = "00"] = String(value).split(":");
  let hour = Number(hourValue);
  if (Number.isNaN(hour)) return String(value);
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minuteValue} ${period}`;
};

const isNewComboCustomization = (parsed) => {
  if (!parsed || typeof parsed !== "object") return false;
  const lineType = String(parsed?.lineType || "").trim().toUpperCase();
  const schemaVersion = String(parsed?.schemaVersion || "").trim();
  return (
    schemaVersion === "1" &&
    lineType === "COMBO" &&
    (parsed?.comboId != null ||
      parsed?.comboName ||
      Array.isArray(parsed?.selectedGroups))
  );
};

/* -------------------------------------------------------------------------- */
/*  Small presentational pieces                                               */
/* -------------------------------------------------------------------------- */

const EventDetailsBlock = ({ event }) => {
  if (!event || typeof event !== "object") return null;

  const hasEventData =
    event.location ||
    event.eventDate ||
    event.startTime ||
    event.endTime;

  if (!hasEventData) return null;

  return (
    <div className="mt-1">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {event.location && (
          <div>
            <p className="text-[10px] text-slate-400">Location</p>
            <p className="text-xs font-medium text-slate-800">
              {event.location}
            </p>
          </div>
        )}

        {event.eventDate && (
          <div>
            <p className="text-[10px] text-slate-400">Date</p>
            <p className="text-xs font-medium text-slate-800">
              {formatEventDate(event.eventDate)}
            </p>
          </div>
        )}

        {event.startTime && (
          <div>
            <p className="text-[10px] text-slate-400">Start Time</p>
            <p className="text-xs font-medium text-slate-800">
              {formatEventTime(event.startTime)}
            </p>
          </div>
        )}

        {event.endTime && (
          <div>
            <p className="text-[10px] text-slate-400">End Time</p>
            <p className="text-xs font-medium text-slate-800">
              {formatEventTime(event.endTime)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const SelectedItemRow = ({ selectedItem, itemIndex }) => {
  if (!selectedItem || typeof selectedItem !== "object") return null;

  const itemName =
    selectedItem.itemName ||
    selectedItem.addonName ||
    selectedItem.productName ||
    selectedItem.name ||
    `Item ${itemIndex + 1}`;

  return (
    <li
      key={`selected-${selectedItem.optionGroupItemId || selectedItem.itemId || selectedItem.addonId || itemIndex}-${itemIndex}`}
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-blue-100 py-1.5 first:border-t-0"
    >
      <span className="text-[11px] font-medium text-gray-700">{itemName}</span>
      {selectedItem.quantityPerPerson != null && (
        <span className="rounded bg-green-50 px-1.5 py-0.5 text-[9px] text-green-700">
          {selectedItem.quantityPerPerson} per person
        </span>
      )}
      {selectedItem.fixedQuantity != null && (
        <span className="rounded bg-yellow-50 px-1.5 py-0.5 text-[9px] text-yellow-700">
          Qty: {selectedItem.fixedQuantity}
        </span>
      )}
      {selectedItem.selectedQuantity != null && (
        <span className="rounded bg-yellow-50 px-1.5 py-0.5 text-[9px] text-yellow-700">
          Qty: {selectedItem.selectedQuantity}
        </span>
      )}
      {Number(selectedItem.extraPrice || 0) > 0 && (
        <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[9px] text-orange-700">
          +₹{Number(selectedItem.extraPrice).toFixed(2)}
        </span>
      )}
      {selectedItem.unitPrice != null && (
        <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[9px] text-orange-700">
          ₹{Number(selectedItem.unitPrice || 0).toFixed(2)}
        </span>
      )}
    </li>
  );
};

const CompactSelectedItem = ({ selectedItem, itemIndex, replacements = [] }) => {
  if (!selectedItem || typeof selectedItem !== "object") return null;

  const originalItemName =
    selectedItem.itemName ||
    selectedItem.addonName ||
    selectedItem.productName ||
    selectedItem.name ||
    `Item ${itemIndex + 1}`;

  const replacement = replacements.find((entry) => {
    const sameOptionGroupItem =
      entry?.fromOptionGroupItemId != null &&
      selectedItem?.optionGroupItemId != null &&
      String(entry.fromOptionGroupItemId) === String(selectedItem.optionGroupItemId);
    const sameItem =
      entry?.fromItemId != null &&
      selectedItem?.itemId != null &&
      String(entry.fromItemId) === String(selectedItem.itemId);
    return sameOptionGroupItem || sameItem;
  });

  const itemName = replacement?.toItemName || originalItemName;

return (
  <span
    className={`inline-flex max-w-full items-center gap-1 rounded-md px-2 py-1 text-[10px] ${
      replacement
        ? "border border-emerald-200 bg-emerald-50 text-emerald-800"
        : "bg-gray-50 text-gray-700"
    }`}
  >
    <span className="font-medium">{itemName}</span>

    {replacement && (
      <span className="rounded bg-white px-1 py-[1px] text-[8px] font-semibold text-emerald-700">
        Replaced
      </span>
    )}

    {selectedItem.quantityPerPerson != null && (
      <span className="rounded bg-green-100 px-1 py-[1px] text-[9px] text-green-700">
        {selectedItem.quantityPerPerson}/person
      </span>
    )}

    {selectedItem.fixedQuantity != null && (
      <span className="text-[9px] text-gray-500">
        ×{selectedItem.fixedQuantity}
      </span>
    )}

    {selectedItem.selectedQuantity != null && (
      <span className="text-[9px] text-gray-500">
        ×{selectedItem.selectedQuantity}
      </span>
    )}

    {Number(selectedItem.extraPrice || 0) > 0 && (
      <span className="text-[9px] font-medium text-orange-600">
        +₹{Number(selectedItem.extraPrice).toFixed(2)}
      </span>
    )}
  </span>
);
};

const ComboSelectedItem = ({ selectedItem, itemIndex }) => {
  if (!selectedItem || typeof selectedItem !== "object") return null;
  const itemName = selectedItem.itemName || selectedItem.name || `Item ${itemIndex + 1}`;

  return (
    <span
      key={`combo-item-${selectedItem.itemId || selectedItem.optionGroupItemId || itemIndex}-${itemIndex}`}
      className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-md bg-gray-50 px-2 py-1 text-[10px] text-gray-700"
    >
      <span className="font-medium">{itemName}</span>
      {selectedItem.variantName && (
        <span className="rounded bg-blue-50 px-1 py-[1px] text-[9px] text-blue-600">
          {selectedItem.variantName}
        </span>
      )}
      {selectedItem.quantity != null && (
        <span className="text-[9px] text-gray-500">×{selectedItem.quantity}</span>
      )}
    </span>
  );
};

const GenericCustomization = ({ parsed }) => {
  const ignoredKeys = new Set([
    "schemaversion", "cartkey", "selectedgroups", "selectedgroupsetmembers",
    "packageaddons", "event", "itemname", "itemid", "categoryid", "categorytype",
    "linetype", "categoryname", "taxpercentage", "taxpercent", "tax_percentage",
    "packageprice", "package_price", "variant", "variantid", "variantname",
  ]);

  const simpleEntries = Object.entries(parsed).filter(([key, value]) => {
    const normalizedKey = String(key).replace(/[_\s-]/g, "").toLowerCase();
    return (
      !ignoredKeys.has(String(key).toLowerCase()) &&
      !ignoredKeys.has(normalizedKey) &&
      value != null &&
      ["string", "number", "boolean"].includes(typeof value) &&
      String(value).trim() !== ""
    );
  });

  if (simpleEntries.length === 0) return null;

  return (
    <div className="mt-1.5 flex max-w-full flex-wrap items-center gap-1">
      {simpleEntries.map(([key, value]) => (
        <span
          key={`${key}-${String(value)}`}
          title={`${formatLabel(key)}: ${String(value)}`}
          className="inline-flex max-w-full items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-[2px] text-[9px] leading-4 text-blue-700"
        >
          <span className="font-semibold">{formatLabel(key)}:</span>
          <span className="ml-1">{String(value)}</span>
        </span>
      ))}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Customization renderer                                                    */
/* -------------------------------------------------------------------------- */

const PackageCustomization = ({
  parsed,
  selectedGroups,
  packageAddons,
  replacements,
  groupSetEntries,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-1.5 overflow-hidden rounded-md border border-slate-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-slate-50"
      >
        <div className="min-w-0">
          <div className="text-[10px] font-semibold text-slate-800">
            Package Customization
            {parsed.packageName && (
              <span className="ml-1.5 font-normal text-slate-500">
                · {parsed.packageName}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {parsed.people != null && (
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
              {parsed.people}p
            </span>
          )}
          {parsed.packageQuantity != null && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">
              ×{parsed.packageQuantity}
            </span>
          )}
          <span className="text-[9px] text-slate-400">
            {isOpen ? "▲" : "▼"}
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-slate-100 text-[10px]">
          {/* Min people / Qty row (only if present) */}
          {(parsed.minimumPeople != null || parsed.packageQuantity != null) && (
            <div className="flex gap-3 border-b border-slate-100 bg-slate-50/60 px-2 py-1 text-[9px] text-slate-500">
              {parsed.minimumPeople != null && (
                <span>Min: <span className="font-medium text-slate-700">{parsed.minimumPeople}</span></span>
              )}
              {parsed.packageQuantity != null && (
                <span>Qty: <span className="font-medium text-slate-700">{parsed.packageQuantity}</span></span>
              )}
            </div>
          )}

          {/* Selected Groups */}
          {selectedGroups.length > 0 && (
            <div className="divide-y divide-slate-100">
              {selectedGroups.map((group, groupIndex) => {
                const groupItems = Array.isArray(group?.items) ? group.items : [];
                return (
                  <div
                    key={`package-${group?.packageOptionGroupId || group?.optionGroupId || groupIndex}-${groupIndex}`}
                    className="px-2 py-1.5"
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="truncate font-semibold text-slate-700">
                        {group?.groupTitle || `Group ${groupIndex + 1}`}
                        <span className="ml-1 font-normal text-slate-400">
                          ({groupItems.length})
                        </span>
                      </span>
                      {(group?.minimumSelection != null || group?.maximumSelection != null) && (
                        <span className="shrink-0 text-[9px] text-slate-400">
                          {group?.minimumSelection != null && `min ${group.minimumSelection}`}
                          {group?.minimumSelection != null && group?.maximumSelection != null && "–"}
                          {group?.maximumSelection != null && `max ${group.maximumSelection}`}
                        </span>
                      )}
                    </div>

                    {groupItems.length > 0 && (
                      <div className="space-y-0.5">
                        {groupItems.map((si, idx) => (
                          <CompactSelectedItem
                            key={idx}
                            selectedItem={si}
                            itemIndex={idx}
                            replacements={replacements}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Group Set Selection */}
          {groupSetEntries.length > 0 && (
            <div className="border-t border-slate-100 px-2 py-1.5">
              <div className="mb-1 font-semibold text-slate-700">Group Sets</div>
              <div className="space-y-1">
                {groupSetEntries.map(([groupName, members]) => {
                  const memberList = Array.isArray(members)
                    ? members
                    : members && typeof members === "object"
                      ? Object.values(members)
                      : [members];

                  return (
                    <div key={groupName}>
                      <div className="mb-0.5 text-[9px] font-medium text-slate-500">
                        {formatLabel(groupName)}
                      </div>
                      <div className="space-y-0.5 pl-1">
                        {memberList.map((member, memberIndex) =>
                          typeof member === "object" ? (
                            <SelectedItemRow
                              key={memberIndex}
                              selectedItem={member}
                              itemIndex={memberIndex}
                            />
                          ) : (
                            <div key={`${groupName}-${memberIndex}`} className="text-slate-700">
                              {String(member)}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Package Add-ons */}
          {packageAddons.length > 0 && (
            <div className="border-t border-slate-100 px-2 py-1.5">
              <div className="mb-1 font-semibold text-slate-700">Add-ons</div>
              <div className="space-y-0.5">
                {packageAddons.map((addon, addonIndex) => (
                  <SelectedItemRow
                    key={addonIndex}
                    selectedItem={addon}
                    itemIndex={addonIndex}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const renderCustomization = (item) => {
  const raw =
    item?.customization || item?.customizations || item?.customization_json;
  const parsed = parseCustomization(raw);
  if (!parsed) return null;

  const lineType = String(parsed.lineType || "").toUpperCase();
  const selectedGroups = Array.isArray(parsed.selectedGroups)
    ? parsed.selectedGroups
    : [];
  const packageAddons = Array.isArray(parsed.packageAddons)
    ? parsed.packageAddons
    : [];
  const replacements = Array.isArray(parsed?.referralCustomizations?.replacements)
    ? parsed.referralCustomizations.replacements
    : [];
  const groupSetEntries =
    parsed.selectedGroupSetMembers &&
    typeof parsed.selectedGroupSetMembers === "object"
      ? Object.entries(parsed.selectedGroupSetMembers)
      : [];

  // ---------- COMBO ----------
  if (isNewComboCustomization(parsed)) {
    return (
      <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50 p-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold text-violet-700">Combo Customization</p>
          <div className="flex flex-wrap items-center gap-1">
            {parsed.comboType && (
              <span className="rounded-full border border-violet-200 bg-white px-2 py-0.5 text-[9px] text-violet-700">
                {parsed.comboType}
              </span>
            )}
            {parsed.isConfigurable && (
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] ${
                  String(parsed.isConfigurable).toUpperCase() === "Y"
                    ? "border-green-200 bg-green-50 text-green-700"
                    : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              >
                {String(parsed.isConfigurable).toUpperCase() === "Y"
                  ? "Configurable"
                  : "Fixed"}
              </span>
            )}
          </div>
        </div>

        {parsed.comboName && (
          <p className="mt-1 text-[12px] font-semibold text-gray-800">{parsed.comboName}</p>
        )}
        {parsed.comboDescription && (
          <p className="mt-0.5 text-[10px] leading-4 text-gray-500">{parsed.comboDescription}</p>
        )}
        {parsed.comboPrice != null && (
          <div className="mt-1.5">
            <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] text-green-700">
              Combo Price: ₹{Number(parsed.comboPrice || 0).toFixed(2)}
            </span>
          </div>
        )}

        {selectedGroups.length > 0 && (
          <div className="mt-2 overflow-hidden rounded-md border border-violet-100 bg-white">
            {selectedGroups.map((group, groupIndex) => {
              const groupItems = Array.isArray(group?.items) ? group.items : [];
              return (
                <div
                  key={`combo-group-${group?.optionGroupId || groupIndex}-${groupIndex}`}
                  className="border-b border-gray-100 px-2 py-1.5 last:border-b-0"
                >
                  <div className="flex items-start gap-2">
                    <div className="w-[125px] shrink-0 pt-0.5">
                      <p className="text-[10px] font-semibold text-violet-700">
                        {group?.groupTitle || `Group ${groupIndex + 1}`}
                      </p>
                      <p className="mt-0.5 text-[10px] text-gray-900">
                        {groupItems.length} selected
                      </p>
                      {(group?.minimumSelection != null || group?.maximumSelection != null) && (
                        <p className="mt-0.5 text-[10px] text-gray-900">
                          {group?.minimumSelection != null && `Min ${group.minimumSelection}`}
                          {group?.minimumSelection != null && group?.maximumSelection != null && " • "}
                          {group?.maximumSelection != null && `Max ${group.maximumSelection}`}
                        </p>
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                      {groupItems.map((si, idx) => (
                        <ComboSelectedItem key={idx} selectedItem={si} itemIndex={idx} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------- GLOBAL ADDON ----------
  if (lineType === "GLOBAL_ADDON") {
    const addonTotal =
      Number(parsed.selectedQuantity || 0) * Number(parsed.unitPrice || 0);
    return (
      <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50 p-2">
        <div className="mb-1 text-[11px] font-semibold text-orange-700">Add-on Details</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {parsed.selectedQuantity != null && (
            <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] text-orange-700">
              Qty: {parsed.selectedQuantity}
            </span>
          )}
          {parsed.unitPrice != null && (
            <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] text-orange-700">
              Unit: ₹{Number(parsed.unitPrice || 0).toFixed(2)}
            </span>
          )}
          {addonTotal > 0 && (
            <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-orange-700">
              Total: ₹{addonTotal.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ---------- PACKAGE ----------
if (lineType === "PACKAGE" || parsed.packageId || parsed.packageName) {
  return (
    <PackageCustomization
      parsed={parsed}
      selectedGroups={selectedGroups}
      packageAddons={packageAddons}
      replacements={replacements}
      groupSetEntries={groupSetEntries}
    />
  );
}

  return <GenericCustomization parsed={parsed} />;
};

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

export default function OrderDetailModal({
  open,
  onClose,
  loading = false,
  order,
  detail,
  onAction,
  onStatusAction,
  isHistoryTab = false,
}) {

  
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const data = detail || order;

  const sMeta = useMemo(
    () => STATUS_META[(data?.status || "").toLowerCase()] || {},
    [data?.status]
  );
  const tMeta = useMemo(
    () => ORDER_TYPE_META[(data?.orderType || "").toLowerCase()] || {},
    [data?.orderType]
  );

  const safeAddress = useMemo(() => {
    if (!data) return "-";
    if (data.address_line) return data.address_line;
    try {
      const obj = JSON.parse(data.address_json || "{}");
      return obj?.values?.address || "-";
    } catch {
      return "-";
    }
  }, [data]);

  const paymentStatus = (data?.payment_status || "").toLowerCase();
  const isUnpaid = paymentStatus === "unpaid";
  const isPaid = paymentStatus === "paid";
  const orderType = (data?.orderType || "").toLowerCase();
  const displayOrderDate = data?.order_date || order?.order_date || "—";

  // Extract event details once (from any item)
  const orderEventDetails = useMemo(() => {
    const items = Array.isArray(data?.items) ? data.items : [];
    for (const item of items) {
      const raw =
        item?.customization || item?.customizations || item?.customization_json;
      const parsed = parseCustomization(raw);
      const event = parsed?.event;
      if (
        event &&
        typeof event === "object" &&
        (event.location || event.eventDate || event.startTime || event.endTime)
      ) {
        return event;
      }
    }
    return null;
  }, [data?.items]);

  if (!open || !order) return null;

  const items = Array.isArray(data?.items) ? data.items : [];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header – fixed */}
        {/* Header – fixed */}
<div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
  <div className="flex items-center gap-3">
    <div className="flex h-9 min-w-[5rem] items-center justify-center rounded-lg bg-emerald-100 px-2">
      <span className="font-bold text-emerald-600">#{order.orderid}</span>
    </div>
    <div>
      <div className="text-lg font-semibold leading-tight">
        {order.organization_name || order.us_code || "—"}
      </div>
      <div className="text-xs text-slate-500">{displayOrderDate}</div>
    </div>
  </div>

  <div className="flex items-center gap-2">
    <StatusBadge
      color={sMeta.color}
      Icon={sMeta.icon}
      label={sMeta.label || data.status || "—"}
    />
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
    >
      <span className="text-lg leading-none">×</span>
    </button>
  </div>
</div>

        {/* Body – scrollable */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-500">
              <Loader2 className="mr-2 animate-spin" size={16} />
              Loading details…
            </div>
          ) : (
            <div className="grid gap-5 p-5 lg:grid-cols-12">
              {/* ---------- LEFT: Customer + Address + Event ---------- */}
             <div className="space-y-3 lg:col-span-3">
  {/* Customer */}
  <div className="rounded-lg border p-3">
    <div className="mb-2 text-sm font-semibold">Customer</div>

    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
      <Row label="Name" value={data.customer_name} />
      <Row label="Mobile" value={data.customer_mobile} />
      <Row label="Order Type" value={tMeta.label || data.orderType} />
      <Row
        label="Payment"
        value={`${data.payment_status || "-"} • ${
          (data.payment_mode || "").toUpperCase() || "-"
        }`}
      />
    </div>

    {onStatusAction && !isHistoryTab && (
      <div className="mt-1">
        {orderType === "home_delivery" ? (
          <button
            type="button"
            onClick={() => onStatusAction("paid", data)}
            disabled={!isPaid}
            className={`w-full rounded-md px-2.5 py-1.5 text-xs font-semibold ${
              isPaid
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "cursor-not-allowed bg-slate-200 text-slate-400"
            }`}
          >
            Dispatch
          </button>
        ) : (
          <div className="space-y-1.5">
            {isUnpaid && (
              <button
                type="button"
                onClick={() => onStatusAction("unpaid", data)}
                className="w-full rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                Cash Payment
              </button>
            )}

            {isPaid && (
              <button
                type="button"
                onClick={() => onStatusAction("paid", data)}
                className="w-full rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                Dispatch
              </button>
            )}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Address */}
  <div className="rounded-lg border p-3">
    <div className="mb-1.5 text-sm font-semibold">Address</div>

    <div className="whitespace-pre-wrap text-xs leading-5 text-slate-700">
      {safeAddress}
    </div>
  </div>

  {/* Event */}
  {orderEventDetails && (
    <div className="rounded-lg border border-pink-200 bg-pink-50/50 p-3">
      <div className="mb-1.5 text-sm font-semibold text-pink-800">
        Event
      </div>

      <EventDetailsBlock event={orderEventDetails} />
    </div>
  )}
</div>

              {/* ---------- MIDDLE: Items (takes more space, independent scroll) ---------- */}
              <div className="flex flex-col rounded-lg border border-slate-200 lg:col-span-5">
  {/* Header */}
  <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
    <div className="text-sm font-semibold text-slate-900">Items</div>

    <span className="text-[11px] font-medium text-slate-500">
      {items.length} {items.length === 1 ? "item" : "items"}
    </span>
  </div>

  {/* Items */}
  <div className="min-h-0 flex-1 overflow-y-auto">
    {items.length === 0 ? (
      <div className="py-8 text-center text-xs text-slate-400">
        No items available.
      </div>
    ) : (
      <div className="divide-y divide-slate-100">
        {items.map((it, i) => (
          <div
            key={it.item_id || it.product_id || i}
            className="px-3 py-2.5 hover:bg-slate-50/50"
          >
            <div className="flex items-start justify-between gap-3">
              {/* Item info */}
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-slate-800">
                  {it.item_name || it.product_id || "Item"}
                </div>

                <div className="mt-0.5 text-[11px] text-slate-400">
                  Qty:{" "}
                  <span className="font-medium text-slate-600">
                    {it.quantity || "-"}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right">
                <div className="text-xs font-semibold text-slate-800">
                  ₹{Number(it.total_price || it.item_price || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* Customization */}
            {renderCustomization(it)}
          </div>
        ))}
      </div>
    )}
  </div>
</div>

              {/* ---------- RIGHT: Totals + Actions ---------- */}
              <div className="space-y-3 lg:col-span-4">
  <div className="rounded-lg border p-3">
    <div className="mb-2 text-sm font-semibold">Totals</div>

    <div className="space-y-1 text-[13px]">
      <Row
        label="Subtotal"
        value={`₹ ${Number(data.subtotal || 0).toFixed(2)}`}
      />
      <Row
        label="Charges"
        value={`₹ ${Number(data.order_charges || data.charges || 0).toFixed(2)}`}
      />
      <Row
        label="GST"
        value={`₹ ${Number(data.order_tax_gst || data.gst || 0).toFixed(2)}`}
      />
      <Row
        label="Discount"
        value={`₹ ${Number(data.discount_amount || 0).toFixed(2)}`}
      />
    </div>

    <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-semibold">
      <span>Total</span>
      <span>
        ₹{Number(data.totalOrderPrice || data.pay_amount || 0).toFixed(2)}
      </span>
    </div>
  </div>

  {!isHistoryTab && (
    <OrderActions order={data} onAction={onAction} onClose={onClose} />
  )}
</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}